/**
 * Announcements API (issue #15) — typed methods over the tenant-aware `api`
 * client. Every call rides the shared Authorization + X-Tenant injection in
 * client.ts; the backend RLS targets the feed and gates the compose authority.
 */
import { api } from "@/lib/api/client";
import { commitOptions, retryOptions, decodeBulkResult, type BulkResult } from "./commitSeams";

export type AnnouncementScope = "academy" | "course";
export type AnnouncementCategory =
  | "academic"
  | "financial"
  | "intensive"
  | "urgent"
  | "general";

export interface Announcement {
  id: string;
  scope: AnnouncementScope;
  course_id: string | null;
  course_title?: string | null;
  category: AnnouncementCategory;
  title: string;
  body: string;
  author_user_id: string | null;
  is_urgent: boolean;
  email_on_post: boolean;
  published_at: string | null;
  is_read: boolean;
}

export interface AnnouncementFeed {
  items: Announcement[];
  unread_count: number;
}

export interface CourseOption {
  id: string;
  code: string;
  title: string;
}

export interface Audiences {
  can_post_academy: boolean;
  courses: CourseOption[];
  email_recipient_counts?: Record<string, number>;
}

export interface CreateAnnouncement {
  scope: AnnouncementScope;
  category: AnnouncementCategory;
  title: string;
  body: string;
  course_id?: string | null;
  is_urgent?: boolean;
  email_on_post?: boolean;
}

/** The POST /announcements response: the created row + fan-out outcome. */
export interface CreateAnnouncementResult extends Announcement {
  notified_count: number;
  email_sent: boolean;
  email_sent_to_count: number;
}

export interface AnnouncementEmailEffect {
  state: "not_requested" | "empty" | "accepted" | "pending" | "failed" | "unknown";
  updated_at: string;
  retry_eligible: boolean;
  counts: {recipients:number;accepted:number;failed:number;unattempted:number;uncertain:number;sending:number};
}
export interface AnnouncementPostResult extends BulkResult {
  announcement_id: string;
  announcement: Announcement;
  email: AnnouncementEmailEffect;
}

/** Validate the effect before drawing Retry. Keep the raw SQL version token intact. */
export function decodeAnnouncementPost(value:unknown):AnnouncementPostResult {
  const bulk=decodeBulkResult(value);
  const item=value as Record<string,unknown>;
  const email=item.email as AnnouncementEmailEffect;
  const announcement=item.announcement as Announcement;
  if(typeof item.announcement_id!=="string" || !announcement || announcement.id!==item.announcement_id
    || !email || !["not_requested","empty","accepted","pending","failed","unknown"].includes(email.state)
    || typeof email.updated_at!=="string" || !Number.isFinite(Date.parse(email.updated_at))
    || typeof email.retry_eligible!=="boolean" || !email.counts
    || ["recipients","accepted","failed","unattempted","uncertain","sending"].some(key=>!Number.isInteger(email.counts[key as keyof typeof email.counts])||email.counts[key as keyof typeof email.counts]<0)
    || bulk.failed.some(part=>part.id!=="email") || !bulk.succeeded.some(part=>part.id==="post")
    || (email.retry_eligible && !bulk.failed.some(part=>part.id==="email")))throw new TypeError("Invalid announcement effect");
  return {...bulk,announcement_id:item.announcement_id,announcement,email};
}

export interface ListParams {
  notice_id?: string;
  course_id?: string;
  scope?: AnnouncementScope;
  teaching?: boolean;
  learning?: boolean;
}

function toQuery(params?: ListParams): string {
  if (!params) return "";
  const q = new URLSearchParams();
  if (params.notice_id) q.set("notice_id",params.notice_id);
  if (params.scope) q.set("scope", params.scope);
  if (params.course_id) q.set("course_id", params.course_id);
  if (params.teaching) q.set("teaching", "true");
  if (params.learning) q.set("learning", "true");
  const s = q.toString();
  return s ? `?${s}` : "";
}

export const announcementsApi = {
  ranking: (teaching=false)=>api.get<import("./inbox").InboxRanking>(`/announcements/ranking${teaching?"?teaching=true":""}`),
  list: (params?: ListParams) =>
    api.get<AnnouncementFeed>(`/announcements${toQuery(params)}`),
  unreadCount: (scope?: AnnouncementScope) =>
    api.get<{ unread_count: number }>(
      `/announcements/unread-count${scope ? `?scope=${scope}` : ""}`,
    ),
  audiences: (teaching = false) =>
    api.get<Audiences>(`/announcements/audiences${teaching ? "?teaching=true" : ""}`),
  create: (body: CreateAnnouncement) =>
    api.post<CreateAnnouncementResult>("/announcements", body),
  createKeyed: async (body:CreateAnnouncement,key:string) => decodeAnnouncementPost(await api.post<unknown>("/announcements",body,commitOptions(key))),
  retryEmail: async (id:string,key:string,version:string) => decodeAnnouncementPost(await api.post<unknown>(`/announcements/${encodeURIComponent(id)}/email/retry`,{ids:["email"],expected_updated_at:version},retryOptions(key))),
  markRead: (id: string) =>
    api.post<{ ok: boolean; read_at: string | null }>(
      `/announcements/${id}/read`,
    ),
};
